// Асинхронная часть вставки: формулы (OMML Word и MathML -> LaTeX на сервере) и картинки (временные файлы Word,
// data:, адреса http(s) через сервер, SVG - растеризуется в браузере в PNG). Загруженные картинки кэшируются по
// адресу в сеансе вставки: повторная вставка (другой режим) не загружает их заново.
import type { JSONContent as J } from '@tiptap/core';
import type { Ctx } from '../../core/registry';
import type { AssetInfo } from '../../core/types';
import { dataUrlToBlob, isImageFile, uploadImage } from '../../core/assets';
import type { MathRef } from './dom';
import { can } from '../auth/session';

export async function resolveMaths(ctx: Ctx, pid: string, omml: string[], maths: MathRef[]): Promise<number> {
  let errors = 0;
  const withOmml = maths.filter((m) => m.omml !== undefined && m.latex === undefined);
  if (withOmml.length) {
    const idx = [...new Set(withOmml.map((m) => m.omml!))];
    const r = await ctx.api.post<{ items: { latex?: string; mathml?: string; error?: string; text?: string }[] }>(
      `/api/projects/${encodeURIComponent(pid)}/paste/math`, { items: idx.map((i) => omml[i] || '') });
    const byIdx = new Map(idx.map((i, k) => [i, r.items[k]]));
    for (const m of withOmml) {
      const x = byIdx.get(m.omml!);
      if (x && x.latex !== undefined && !x.error) { m.latex = x.latex; m.mathml = x.mathml || undefined; } else {
        errors++;
        m.error = x?.error || 'не преобразована';
        m.text = x?.text || '';
        m.latex = x?.text ? `\\text{${x.text.replace(/[{}\\]/g, '')}}` : '';
      }
    }
  }
  const withMml = maths.filter((m) => m.mathml && m.latex === undefined);
  await pool(withMml, 4, async (m) => {
    try { m.latex = (await ctx.api.mathConvert(m.mathml!, 'mathml', 'latex')).value; } catch (e: any) {
      errors++;
      m.error = e.message;
      m.latex = '';
    }
  });
  for (const m of maths) if (m.latex === undefined) m.latex = '';
  return errors;
}

/** Картинка по адресу -> имя в assets проекта (или ошибка). */
async function fetchImage(ctx: Ctx, pid: string, src: string): Promise<AssetInfo> {
  if (src.startsWith('file:')) {
    // временные файлы Word лежат на компьютере пользователя: сервер читает их, только если это его же компьютер
    // (администратор на компьютере-сервере); остальным - место под рисунок и подсказка вставить картинку отдельно
    if (!can('canPasteLocalFiles')) throw new Error('Картинки из временных файлов Word через сеть не передаются - вставьте картинку отдельно (скопируйте в Word только её и вставьте на место рамки).');
    return ctx.api.pasteLocalImage(pid, src);
  }
  if (/^data:image\/svg/i.test(src)) return rasterizeSvg(decodeSvgData(src));
  if (src.startsWith('data:image/')) return uploadImage(dataUrlToBlob(src), 'вставка.png');
  if (src.startsWith('blob:')) {
    const b = await (await fetch(src)).blob();
    return uploadImage(b, 'вставка.png');
  }
  if (/^https?:/i.test(src)) {
    const r = await ctx.api.post<AssetInfo & { svg?: string }>(`/api/projects/${encodeURIComponent(pid)}/paste/web-image`, { url: src });
    if (r.svg) return rasterizeSvg(r.svg);
    return r;
  }
  throw new Error('адрес картинки недоступен (' + src.slice(0, 60) + ')');
}

function decodeSvgData(src: string): string {
  const [head, data] = src.split(',', 2);
  return head.includes(';base64') ? decodeURIComponent(escape(atob(data))) : decodeURIComponent(data);
}

/** SVG -> PNG (Word не показывает SVG в отчёте по СТО надёжно): рисуем в canvas с масштабом 2. */
async function rasterizeSvg(svg: string): Promise<AssetInfo> {
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
  try {
    const img = new Image();
    await new Promise<void>((ok, bad) => { img.onload = () => ok(); img.onerror = () => bad(new Error('SVG не отрисовался')); img.src = url; });
    let w = img.naturalWidth || 600;
    let h = img.naturalHeight || 400;
    const k = Math.min(2, 2000 / Math.max(w, h));
    w = Math.round(w * k); h = Math.round(h * k);
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const g = c.getContext('2d')!;
    g.fillStyle = '#fff';
    g.fillRect(0, 0, w, h);
    g.drawImage(img, 0, 0, w, h);
    const blob = await new Promise<Blob>((ok, bad) => c.toBlob((b) => (b ? ok(b) : bad(new Error('PNG не получен'))), 'image/png'));
    return uploadImage(blob, 'рисунок.png');
  } finally {
    URL.revokeObjectURL(url);
  }
}

export interface ImageReport { loaded: number; failed: number; reasons: string[] }

/** Загрузить картинки рисунков (attrs.__img); неудачные остаются местом под рисунок (src = ''). */
export async function resolveImages(ctx: Ctx, pid: string, figures: J[], files: File[], cache: Map<string, string>,
  progress?: (done: number, total: number) => void): Promise<ImageReport> {
  const rep: ImageReport = { loaded: 0, failed: 0, reasons: [] };
  const spare = files.filter(isImageFile);
  const todo = figures.filter((f) => (f as any).__img);
  let done = 0;
  await pool(todo, 4, async (f) => {
    const src = String((f as any).__img);
    try {
      let name = cache.get(src);
      if (!name) {
        name = (await fetchImage(ctx, pid, src)).name;
        cache.set(src, name);
      }
      f.attrs = { ...f.attrs, src: name };
      rep.loaded++;
    } catch (e: any) {
      // картинка из временного файла недоступна, но в буфере есть файлы-картинки - берём по порядку
      const file = spare.shift();
      if (file) {
        try {
          const a = await uploadImage(file, file.name || 'вставка.png');
          f.attrs = { ...f.attrs, src: a.name };
          rep.loaded++;
          return;
        } catch { /* ниже - как неудача */ }
      }
      rep.failed++;
      const msg = String(e?.message || e);
      if (!rep.reasons.includes(msg)) rep.reasons.push(msg);
    } finally {
      progress?.(++done, todo.length);
    }
  });
  return rep;
}

async function pool<T>(arr: T[], n: number, fn: (x: T) => Promise<void>) {
  let i = 0;
  const workers = Array.from({ length: Math.min(n, arr.length) }, async () => {
    while (i < arr.length) { const k = i++; await fn(arr[k]); }
  });
  await Promise.all(workers);
}
